import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Chip,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import VideocamIcon from '@mui/icons-material/Videocam'
import PersonIcon from '@mui/icons-material/Person'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { formatEventTimeRange } from 'src/supportPortal/components/common/bookedEventInfo/components/utils'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { EVENT_TYPES_ARRAY, BOOKED_EVENT_MODES } from 'src/consumer/constants'

dayjs.extend(utc)

const STATUS_COLORS = {
  starting: 'info',
  started: 'success',
  'patient-joined': 'success',
}

const STATUS_LABELS = {
  starting: 'Starting',
  started: 'Started',
  'patient-joined': 'Patient Joined',
}

/**
 * VideoCallModal Component
 * 
 * Displays information about a video call appointment that has started.
 * 
 * Props:
 * - open: boolean - Whether the modal is open
 * - bookedEventInfo: object - The booked event information
 * - status: string - Status of the video call ("starting" | "started" | "patient-joined")
 * - receivedAt: string - ISO timestamp when the signal was received
 * - onClose: function - Callback to close the modal
 * 
 * Behavior:
 * - Modal can be closed by clicking the X button or clicking outside
 * - Modal automatically closes after 15 minutes from when it was received
 */
export default function VideoCallModal({ open, bookedEventInfo, status, receivedAt, onClose }) {
  const [timeRemaining, setTimeRemaining] = useState(null)

  useEffect(() => {
    if (!open || !receivedAt) return

    // Calculate time remaining until 15 minutes
    const receivedTime = dayjs(receivedAt)
    const expiryTime = receivedTime.add(15, 'minutes')
    const now = dayjs()

    if (now.isAfter(expiryTime)) {
      // Already past 15 minutes, close immediately
      onClose()
      return
    }

    // Update time remaining every second
    const updateTimeRemaining = () => {
      const remaining = expiryTime.diff(dayjs(), 'seconds')
      if (remaining <= 0) {
        onClose()
        return
      }
      setTimeRemaining(remaining)
    }

    updateTimeRemaining()
    const interval = setInterval(updateTimeRemaining, 1000)

    // Check if we should auto-close
    const checkAutoClose = () => {
      if (dayjs().isAfter(expiryTime)) {
        onClose()
      }
    }

    const autoCloseInterval = setInterval(checkAutoClose, 60000) // Check every minute

    return () => {
      clearInterval(interval)
      clearInterval(autoCloseInterval)
    }
  }, [open, receivedAt, onClose])

  if (!bookedEventInfo) return null

  const currentEventType = EVENT_TYPES_ARRAY?.find(
    (event) => event?.id === bookedEventInfo?.eventType,
  )
  const currentMode = BOOKED_EVENT_MODES?.[bookedEventInfo?.meetingSrc]

  const formatTimeRemaining = (seconds) => {
    if (seconds < 60) return `${seconds}s`
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}m ${secs}s`
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VideocamIcon color="primary" />
          <Typography variant="h6">Video Call Notification</Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Status Chip */}
          {status && (
            <Box>
              <Chip
                label={STATUS_LABELS[status] || status}
                color={STATUS_COLORS[status] || 'default'}
                size="small"
              />
            </Box>
          )}

          {/* Event Type and Mode */}
          <Box>
            <Typography variant="body2" color="text.secondary">
              Session Type
            </Typography>
            <Typography variant="body1">
              {currentMode?.altTitle || currentMode?.title || 'Video Call'}{' '}
              {currentEventType?.title || bookedEventInfo?.eventType}
            </Typography>
          </Box>

          {/* Patient Information */}
          {bookedEventInfo?.patientInfo && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon color="action" />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Patient
                </Typography>
                <Typography variant="body1">
                  {bookedEventInfo.patientInfo.name ||
                    bookedEventInfo.userName ||
                    'Unknown Patient'}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Time Information */}
          {bookedEventInfo?.startTime && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTimeIcon color="action" />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Scheduled Time
                </Typography>
                <Typography variant="body1">
                  {formatEventTimeRange(bookedEventInfo)}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Mentor Information */}
          {bookedEventInfo?.mentorName && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Mentor
              </Typography>
              <Typography variant="body1">{bookedEventInfo.mentorName}</Typography>
            </Box>
          )}

          {/* Auto-close timer (if less than 5 minutes remaining) */}
          {timeRemaining && timeRemaining < 300 && (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              This notification will auto-close in {formatTimeRemaining(timeRemaining)}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

